import type { Ref } from 'vue'
import type { LocalizableResource } from '~/composables/types/localizable'
import { useLocaleUrlBuilder } from '~/composables/locale/useLocaleUrlBuilder'

export const useLocaleSwitcher = (
  resource: Ref<LocalizableResource | null | undefined>
) => {
  const urlBuilder = useLocaleUrlBuilder()
  const switchLocalePath = useSwitchLocalePath()
  const { locales } = useI18n()
  const { origin } = useRequestURL()

  /**
   * When the page has Strapi localizations, only expose locales that actually
   * have a corresponding entry (the resource's own locale + each localization).
   * For pages without a resource (e.g. checkout), expose all app locales.
   */
  const availableLocales = computed(() => {
    if (!resource.value?.localizations) return locales.value

    const availableCodes = new Set([
      resource.value.locale?.toLowerCase(),
      ...resource.value.localizations.map((l) => l.locale?.toLowerCase()),
    ])
    return locales.value.filter((l) => availableCodes.has(l.code.toLowerCase()))
  })

  const shouldShowLanguagePicker = computed(() => {
    if (!resource.value?.localizations?.length) return true
    const currentLocale = resource.value.locale?.toLowerCase()
    return resource.value.localizations.some(
      (loc) => loc.locale?.toLowerCase() !== currentLocale
    )
  })

  const getLocaleUrl = (localeCode: string): string => {
    if (resource.value?.localizations) {
      const target = resource.value.localizations.find(
        (loc) => loc.locale?.toLowerCase() === localeCode.toLowerCase()
      )
      if (target) {
        const customUrl = urlBuilder(target, localeCode)
        if (customUrl) {
          const path = customUrl.endsWith('/') ? customUrl : customUrl + '/'
          return origin + path
        }
      }
    }

    const defaultUrl = switchLocalePath(localeCode as any)
    const path = defaultUrl.endsWith('/') ? defaultUrl : defaultUrl + '/'
    return origin + path
  }

  return { availableLocales, shouldShowLanguagePicker, getLocaleUrl }
}
